import { useState, useEffect } from 'react';
import { Key, CheckCircle, XCircle, Clock, Power, PowerOff, Copy, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { getAdminCodes, disableCode, enableCode, getCustomers, assignCustomerToCode } from '@/lib/api';

const AdminCodes = () => {
  const [codes, setCodes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [codesRes, customersRes] = await Promise.all([
        getAdminCodes(),
        getCustomers()
      ]);
      setCodes(codesRes.data.codes);
      setCustomers(customersRes.data.customers);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (codeId, currentStatus) => {
    setActionLoading(codeId);
    try {
      if (currentStatus) {
        await disableCode(codeId);
        toast.success('Code disabled');
      } else {
        await enableCode(codeId);
        toast.success('Code enabled');
      }
      setCodes(codes.map(c => 
        c.id === codeId ? { ...c, is_active: !currentStatus } : c
      ));
    } catch (error) {
      toast.error('Failed to update code status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignCustomer = async (codeId, customerId) => {
    setActionLoading(codeId + '-assign');
    try {
      await assignCustomerToCode(codeId, customerId === 'none' ? null : customerId);
      setCodes(codes.map(c => 
        c.id === codeId ? { ...c, customer_id: customerId === 'none' ? null : customerId } : c
      ));
      toast.success(customerId === 'none' ? 'Customer unassigned' : 'Customer assigned');
    } catch (error) {
      toast.error('Failed to assign customer');
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copied to clipboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return (
        <Badge variant="outline" className="border-red-500/50 text-red-400 bg-red-500/10">
          <PowerOff className="w-3 h-3 mr-1" />
          Disabled
        </Badge>
      );
    }
    if (isExpired(code.expires_at)) {
      return (
        <Badge variant="outline" className="border-amber-500/50 text-amber-400 bg-amber-500/10">
          <Clock className="w-3 h-3 mr-1" />
          Expired
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 bg-emerald-500/10">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </Badge>
    );
  };

  const getCustomerName = (customerId) => {
    if (!customerId) return null;
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-foreground">Access Codes</h1>
            <p className="text-muted-foreground text-sm">
              Manage pricing access codes and assign customer pricing profiles
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-sm text-muted-foreground">
                {codes.filter(c => c.is_active && !isExpired(c.expires_at)).length} Active
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm text-muted-foreground">
                {codes.filter(c => !c.is_active).length} Disabled
              </span>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Customer Pricing Assignment</p>
                <p className="text-sm text-muted-foreground">
                  Assign a customer profile to an access code to show custom pricing. 
                  Users with assigned customers will see adjusted fees when accessing /pricing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="glass-card">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <span className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : codes.length === 0 ? (
              <div className="text-center py-12">
                <Key className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">No access codes generated yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Codes are created when NDA requests are approved
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="table-zebra" data-testid="codes-table">
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="font-semibold text-foreground">Access Code</TableHead>
                      <TableHead className="font-semibold text-foreground">Email</TableHead>
                      <TableHead className="font-semibold text-foreground">Customer Profile</TableHead>
                      <TableHead className="font-semibold text-foreground">Created</TableHead>
                      <TableHead className="font-semibold text-foreground">Last Used</TableHead>
                      <TableHead className="font-semibold text-foreground">Status</TableHead>
                      <TableHead className="font-semibold text-foreground text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.id} className="border-border">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-sm text-primary bg-primary/10 px-2 py-1 rounded">
                              {code.code}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(code.code)}
                              data-testid={`copy-code-${code.id}`}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{code.email}</TableCell>
                        <TableCell>
                          <Select 
                            value={code.customer_id || 'none'} 
                            onValueChange={(value) => handleAssignCustomer(code.id, value)}
                            disabled={actionLoading === code.id + '-assign'}
                          >
                            <SelectTrigger 
                              className="w-48 h-8 text-sm bg-background/50" 
                              data-testid={`customer-select-${code.id}`}
                            >
                              <SelectValue placeholder="No customer assigned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                <span className="text-muted-foreground">No customer (Master pricing)</span>
                              </SelectItem>
                              {customers.map(customer => (
                                <SelectItem key={customer.id} value={customer.id}>
                                  {customer.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(code.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(code.last_used_at)}
                        </TableCell>
                        <TableCell>{getStatusBadge(code)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant={code.is_active ? 'outline' : 'default'}
                            size="sm"
                            onClick={() => handleToggleStatus(code.id, code.is_active)}
                            disabled={actionLoading === code.id}
                            className={code.is_active ? 'border-destructive/50 text-destructive hover:bg-destructive/10' : ''}
                            data-testid={`toggle-code-${code.id}`}
                          >
                            {actionLoading === code.id ? (
                              <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : code.is_active ? (
                              <>
                                <PowerOff className="w-4 h-4 mr-1" />
                                Disable
                              </>
                            ) : (
                              <>
                                <Power className="w-4 h-4 mr-1" />
                                Enable
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCodes;
